import React, { useEffect, useMemo, useRef, useState } from 'react';
import { cn } from '@/utils/cn';
import { useTheme } from '@/hooks/useTheme';

export interface OtpInputProps {
  value: string;
  onChange: (value: string) => void;
  length?: number;
  disabled?: boolean;
  autoFocus?: boolean;
  'aria-label'?: string;
  className?: string;
  inputClassName?: string;
}

export const OtpInput: React.FC<OtpInputProps> = ({
  value,
  onChange,
  length = 6,
  disabled = false,
  autoFocus = false,
  'aria-label': ariaLabel = 'One-time code',
  className,
  inputClassName,
}) => {
  const { isDark } = useTheme();

  const normalizedValue = useMemo(() => value.replace(/\D/g, '').slice(0, length), [value, length]);
  const [digits, setDigits] = useState<string[]>(
    () => Array.from({ length }, (_, i) => normalizedValue[i] ?? '')
  );

  const inputRefs = useRef<Array<HTMLInputElement | null>>([]);

  useEffect(() => {
    setDigits(Array.from({ length }, (_, i) => normalizedValue[i] ?? ''));
  }, [normalizedValue, length]);

  useEffect(() => {
    if (!autoFocus) return;
    const firstEmpty = digits.findIndex((d) => !d);
    const idx = firstEmpty === -1 ? Math.max(0, length - 1) : firstEmpty;
    inputRefs.current[idx]?.focus();
  }, [autoFocus, digits, length]);

  const emit = (nextDigits: string[]) => {
    const nextValue = nextDigits.join('');
    setDigits(nextDigits);
    onChange(nextValue);
  };

  const focusIndex = (index: number) => {
    inputRefs.current[index]?.focus();
  };

  const fillFromIndex = (startIndex: number, raw: string) => {
    const digitsOnly = raw.replace(/\D/g, '');
    if (!digitsOnly) return;

    const next = [...digits];
    let i = startIndex;
    for (const ch of digitsOnly) {
      if (i >= length) break;
      next[i] = ch;
      i += 1;
    }
    emit(next);
    focusIndex(Math.min(i, length - 1));
  };

  return (
    <div
      className={cn('grid w-full gap-2', className)}
      style={{ gridTemplateColumns: `repeat(${length}, minmax(0, 1fr))` }}
      aria-label={ariaLabel}
    >
      {Array.from({ length }, (_, index) => (
        <input
          key={index}
          ref={(el) => { inputRefs.current[index] = el; }}
          value={digits[index] ?? ''}
          disabled={disabled}
          inputMode="numeric"
          pattern="\d*"
          type="text"
          autoCapitalize="off"
          autoCorrect="off"
          spellCheck={false}
          maxLength={1}
          aria-label={`Digit ${index + 1}`}
          autoComplete="off"
          data-bwignore="true"
          data-lpignore="true"
          data-1p-ignore="true"
          data-form-type="other"
          className={cn(
            'h-12 w-full rounded-lg border text-center text-lg font-semibold',
            'focus:outline-none focus:ring-2 focus:ring-offset-1',
            isDark
              ? 'bg-secondary-700 border-secondary-600 text-secondary-100 focus:ring-primary-500/50'
              : 'bg-white border-secondary-200 text-secondary-900 focus:ring-primary-500/50',
            inputClassName
          )}
          onFocus={(e) => e.currentTarget.select()}
          onPaste={(e) => {
            e.preventDefault();
            fillFromIndex(index, e.clipboardData.getData('text'));
          }}
          onChange={(e) => {
            const raw = e.target.value;
            const digitsOnly = raw.replace(/\D/g, '');

            // Sometimes multiple chars arrive in one change (mobile, autofill, paste).
            if (digitsOnly.length > 1) {
              fillFromIndex(index, digitsOnly);
              return;
            }

            const next = [...digits];
            next[index] = digitsOnly.slice(0, 1) || '';
            emit(next);

            if (digitsOnly && index < length - 1) {
              focusIndex(index + 1);
            }
          }}
          onKeyDown={(e) => {
            if (e.key === 'Backspace') {
              e.preventDefault();
              const next = [...digits];

              // Requirement: clear current and move focus to previous.
              if (next[index]) {
                next[index] = '';
                emit(next);
                if (index > 0) focusIndex(index - 1);
                return;
              }

              if (index > 0) {
                next[index - 1] = '';
                emit(next);
                focusIndex(index - 1);
              }
              return;
            }

            if (e.key === 'ArrowLeft') {
              if (index > 0) focusIndex(index - 1);
              return;
            }

            if (e.key === 'ArrowRight') {
              if (index < length - 1) focusIndex(index + 1);
              return;
            }
          }}
        />
      ))}
    </div>
  );
};

export default OtpInput;


